import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { ToastService } from '../../services/toast.service';
import { ConfirmService } from '../../services/confirm.service';
import { Preventivo, Cliente, PreventivoRiga, VoceFrequente } from '../../models';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-preventivo-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatSelectModule],
  template: `
    <div class="page-container">
      <div class="header-flex">
        <div>
          <h1>{{ isNew ? 'Nuovo Preventivo' : (preventivo.numero || 'Preventivo #' + preventivo.id) }}</h1>
          <p class="subtitle">Compila i dettagli e le voci del preventivo di pittura</p>
        </div>
        <div class="action-bar" *ngIf="!isNew">
          <button class="btn-success" (click)="trasformaInFattura()" *ngIf="!preventivo.is_converted" title="Crea fattura da questo preventivo">
            <span class="material-icons" style="font-size: 18px;">receipt</span> Trasforma in Fattura
          </button>
          <button class="btn-secondary" (click)="duplicaPreventivo()" title="Copia preventivo per un altro cliente">
            <span class="material-icons" style="font-size: 18px;">content_copy</span> Duplica
          </button>
          <button class="btn-secondary" (click)="downloadPdf()">
            <span class="material-icons" style="font-size: 18px;">picture_as_pdf</span> PDF
          </button>
          <button class="btn-secondary" (click)="sendEmail()">
            <span class="material-icons" style="font-size: 18px;">email</span> Email
          </button>
        </div>
      </div>

      <!-- Warning Banner se già fatturato -->
      <div *ngIf="preventivo.is_converted" class="warning-banner card">
        <div class="warning-content">
          <span class="material-icons warning-icon">lock</span>
          <div>
            <strong>Preventivo già fatturato ({{ preventivo.fattura_numero }})</strong>
            <p>Questo preventivo è stato trasformato in fattura. Per sicurezza, le modifiche sono bloccate di standard.</p>
          </div>
        </div>
        <button type="button" class="btn-secondary" (click)="unlockEditing()">Sblocca Modifiche</button>
      </div>

      <div class="card form-card">
        <div class="form-row">
          <div class="form-group">
            <label>Cliente *</label>
            <mat-form-field appearance="outline">
              <mat-select [(ngModel)]="preventivo.cliente_id" name="cliente_id" [disabled]="isLocked">
                <mat-option [value]="undefined">Seleziona cliente...</mat-option>
                <mat-option *ngFor="let c of clienti" [value]="c.id">{{ c.cognome }} {{ c.nome }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="form-group">
            <label>Data *</label>
            <input type="date" [(ngModel)]="preventivo.data" name="data" [disabled]="isLocked" />
          </div>
          <div class="form-group">
            <label>Stato</label>
            <mat-form-field appearance="outline">
              <mat-select [(ngModel)]="preventivo.stato" name="stato" [disabled]="isLocked">
                <mat-option value="bozza">Bozza</mat-option>
                <mat-option value="accettato">Accettato</mat-option>
                <mat-option value="scaduto">Scaduto</mat-option>
                <mat-option value="annullato">Annullato</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <div class="form-group">
          <label>Note</label>
          <textarea [(ngModel)]="preventivo.note" name="note" rows="2" placeholder="Note o condizioni di pagamento..." [disabled]="isLocked"></textarea>
        </div>

        <!-- Opzioni IVA -->
        <div class="iva-card-options" style="margin-top: 16px; margin-bottom: 20px; padding: 14px 16px; background: var(--bg-main, #f8f9fa); border-radius: 8px; border: 1px solid var(--border, #e2e8f0); display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <input type="checkbox" id="mostraIvaCheckbox" [(ngModel)]="preventivo.mostra_iva" name="mostra_iva" [disabled]="isLocked" style="width: 18px; height: 18px; cursor: pointer;" />
            <label for="mostraIvaCheckbox" style="font-weight: 600; cursor: pointer; margin: 0; font-size: 14px; color: var(--text-main);">Mostra IVA nel preventivo</label>
          </div>
          <div class="form-group" *ngIf="preventivo.mostra_iva" style="margin: 0; min-width: 200px;">
            <label style="font-size: 12px; font-weight: 600; color: var(--text-muted); display: block; margin-bottom: 4px;">Aliquota IVA (%)</label>
            <mat-form-field appearance="outline" style="width: 100%;">
              <mat-select [(ngModel)]="preventivo.aliquota_iva" name="aliquota_iva" [disabled]="isLocked">
                <mat-option [value]="22">22% (Ordinaria)</mat-option>
                <mat-option [value]="10">10% (Ristrutturazioni / Edilizia)</mat-option>
                <mat-option [value]="4">4% (Prima casa)</mat-option>
                <mat-option [value]="0">0% (Esente / Non imponibile)</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <div class="section-voci-header">
          <h3>Voci Lavori</h3>
          <button type="button" class="btn-secondary btn-preset" (click)="showVociModal = true" *ngIf="!isLocked">
            <span class="material-icons">bolt</span> Inserisci da Voci Frequenti
          </button>
        </div>

        <!-- Desktop Table View -->
        <table class="items-table desktop-only">
          <thead>
            <tr>
              <th>Descrizione Intervento</th>
              <th width="100">Quantità</th>
              <th width="130">Prezzo Unit. (€)</th>
              <th width="130">Totale (€)</th>
              <th width="60" *ngIf="!isLocked"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let r of righe; let i = index">
              <td><input type="text" [(ngModel)]="r.descrizione" name="descrizione_{{i}}" placeholder="Es. Tinteggiatura pareti soggiorno..." [disabled]="isLocked" /></td>
              <td><input type="number" [(ngModel)]="r.quantitativo" name="quantitativo_{{i}}" min="1" (input)="calcTotal()" [disabled]="isLocked" /></td>
              <td><input type="number" [(ngModel)]="r.prezzo_unitario" name="prezzo_{{i}}" step="0.01" (input)="calcTotal()" [disabled]="isLocked" /></td>
              <td class="text-right"><strong>€ {{ (r.quantitativo * r.prezzo_unitario) | number:'1.2-2' }}</strong></td>
              <td *ngIf="!isLocked">
                <button type="button" class="btn-danger-sm" (click)="removeRiga(i)" title="Elimina riga">
                  <span class="material-icons" style="font-size: 16px;">close</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Mobile Stacked Card View for Items -->
        <div class="mobile-only mobile-items-list">
          <div class="mobile-riga-card" *ngFor="let r of righe; let i = index">
            <div class="m-riga-header">
              <span>Voce #{{ i + 1 }}</span>
              <button type="button" class="btn-danger-sm" (click)="removeRiga(i)" *ngIf="!isLocked" title="Elimina riga">
                <span class="material-icons" style="font-size: 16px;">close</span>
              </button>
            </div>
            <div class="form-group" style="margin-bottom: 0;">
              <label>Descrizione Intervento *</label>
              <input type="text" [(ngModel)]="r.descrizione" name="m_descrizione_{{i}}" placeholder="Es. Tinteggiatura pareti..." [disabled]="isLocked" />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Quantità</label>
                <input type="number" [(ngModel)]="r.quantitativo" name="m_quantitativo_{{i}}" min="1" (input)="calcTotal()" [disabled]="isLocked" />
              </div>
              <div class="form-group">
                <label>Prezzo Unit. (€)</label>
                <input type="number" [(ngModel)]="r.prezzo_unitario" name="m_prezzo_{{i}}" step="0.01" (input)="calcTotal()" [disabled]="isLocked" />
              </div>
            </div>
            <div class="m-riga-total">
              <span>Totale Voce:</span>
              <strong>€ {{ (r.quantitativo * r.prezzo_unitario) | number:'1.2-2' }}</strong>
            </div>
          </div>
        </div>

        <button type="button" class="btn-dashed" (click)="addRiga()" *ngIf="!isLocked">
          <span class="material-icons" style="font-size: 18px;">add</span> Aggiungi Voce di Lavoro
        </button>

        <div class="total-section" style="margin-top: 20px; padding: 16px; background: var(--bg-main, #f8f9fa); border-radius: 8px; border: 1px solid var(--border, #e2e8f0);">
          <div *ngIf="preventivo.mostra_iva" style="display: flex; flex-direction: column; gap: 6px; text-align: right; max-width: 320px; margin-left: auto;">
            <div style="display: flex; justify-content: space-between; font-size: 14px; color: var(--text-muted);">
              <span>Imponibile:</span>
              <strong>€ {{ calculateSubtotal() | number:'1.2-2' }}</strong>
            </div>
            <div style="display: flex; justify-content: space-between; font-size: 14px; color: var(--text-muted);">
              <span>IVA ({{ preventivo.aliquota_iva || 22 }}%):</span>
              <strong>€ {{ calculateIvaAmount() | number:'1.2-2' }}</strong>
            </div>
            <div style="border-top: 1px solid var(--border, #e2e8f0); padding-top: 8px; display: flex; justify-content: space-between; font-size: 18px; font-weight: 700; color: var(--primary, #C85A32);">
              <span>Totale Preventivo:</span>
              <span>€ {{ calculateTotal() | number:'1.2-2' }}</span>
            </div>
          </div>
          <div *ngIf="!preventivo.mostra_iva" style="text-align: right;">
            <h2 style="margin: 0; font-size: 20px; font-weight: 700; color: var(--primary, #C85A32);">Totale Preventivo: € {{ calculateTotal() | number:'1.2-2' }}</h2>
          </div>
        </div>

        <div class="footer-actions">
          <button type="button" class="btn-secondary" (click)="router.navigate(['/preventivi'])">Annulla</button>
          <button type="button" class="btn-primary" (click)="save()" [disabled]="isLocked">Salva Preventivo</button>
        </div>
      </div>
    </div>

    <!-- Modal Voci Frequenti -->
    <div class="modal-backdrop" *ngIf="showVociModal">
      <div class="modal-card">
        <h2>⚡ Seleziona da Voci Frequenti</h2>
        <p class="subtitle">Scegli una lavorazione predefinita dalla tua libreria:</p>
        
        <!-- Search input for preset items -->
        <div style="margin-top: 14px; margin-bottom: 12px; padding: 10px 14px; display: flex; align-items: center; gap: 8px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-main);">
          <span class="material-icons" style="font-size: 18px; color: var(--text-muted);">search</span>
          <input type="text" [(ngModel)]="vociSearchTerm" placeholder="Cerca voce frequente..." style="border: none; background: transparent; outline: none; width: 100%; font-size: 14px; color: var(--text-main);" />
          <button *ngIf="vociSearchTerm" class="btn-clear" (click)="vociSearchTerm = ''" style="background: var(--border); border: none; padding: 2px 6px; border-radius: 4px; font-size: 11px; cursor: pointer;">X</button>
        </div>

        <div class="voci-preset-list" *ngIf="filteredVociFrequenti.length > 0; else noVoci">
          <div class="voce-preset-item" *ngFor="let v of filteredVociFrequenti" (click)="aggiungiVocePreset(v)">
            <div class="vp-info">
              <strong>{{ v.descrizione }}</strong>
              <span>€ {{ v.prezzo_unitario | number:'1.2-2' }}</span>
            </div>
            <span class="material-icons">add_circle</span>
          </div>
        </div>
        <ng-template #noVoci>
          <p class="text-center" style="padding: 20px 0; color: var(--text-muted);">Nessuna voce trovata.</p>
        </ng-template>
        <div class="modal-actions" style="margin-top: 20px;">
          <button type="button" class="btn-secondary" (click)="showVociModal = false">Chiudi</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .page-container { max-width: 1000px; margin: 0 auto; }
    .header-flex { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
    h1 { margin: 0; color: var(--text-main); font-size: 26px; font-weight: 700; letter-spacing: -0.5px; }
    .subtitle { color: var(--text-muted); margin: 4px 0 0; font-size: 14px; }
    
    .warning-banner {
      background: rgba(217, 119, 6, 0.12);
      border: 1px solid var(--accent);
      padding: 16px 24px;
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      border-radius: 16px;
      flex-wrap: wrap;
    }
    .warning-content {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .warning-icon { color: var(--accent); font-size: 28px; }
    .warning-content strong { color: var(--text-main); font-size: 15px; display: block; }
    .warning-content p { margin: 2px 0 0 0; color: var(--text-muted); font-size: 13px; }

    .card { background: var(--surface); padding: 32px; border-radius: 16px; box-shadow: var(--shadow); border: 1px solid var(--border); color: var(--text-main); }
    .form-row { display: flex; gap: 16px; }
    .form-group { margin-bottom: 18px; flex: 1; }
    label { display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px; color: var(--text-main); }
    input, select, textarea { width: 100%; padding: 11px 14px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; box-sizing: border-box; background: var(--bg-main); color: var(--text-main); transition: all 0.2s; }
    input:focus, select:focus, textarea:focus { border-color: var(--primary); outline: none; background: var(--surface); box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15); }
    input:disabled, select:disabled, textarea:disabled { opacity: 0.7; cursor: not-allowed; background: var(--bg-main); }
    
    .section-voci-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 32px;
      margin-bottom: 16px;
      border-bottom: 1px solid var(--border);
      padding-bottom: 8px;
    }
    .section-voci-header h3 { margin: 0; border: none; padding: 0; }
    .btn-preset { padding: 6px 14px; font-size: 13px; display: inline-flex; align-items: center; gap: 4px; }
    
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    .items-table th, .items-table td { padding: 12px; border-bottom: 1px solid var(--border); vertical-align: middle; font-size: 14px; color: var(--text-main); }
    .items-table th { background: var(--bg-main); font-weight: 600; font-size: 12px; color: var(--text-muted); text-transform: uppercase; }
    .text-right { text-align: right; font-weight: 600; }
    
    .mobile-only { display: none !important; }
    .desktop-only { display: block !important; }

    .mobile-items-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 20px;
    }
    .mobile-riga-card {
      background: var(--bg-main);
      border: 1px solid var(--border);
      border-radius: 12px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .m-riga-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 700;
      font-size: 13px;
      color: var(--text-muted);
      border-bottom: 1px solid var(--border);
      padding-bottom: 8px;
    }
    .m-riga-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: var(--surface);
      padding: 10px 14px;
      border-radius: 8px;
      border: 1px solid var(--border);
      font-size: 14px;
      color: var(--text-main);
    }

    .btn-dashed { width: 100%; padding: 12px; border: 2px dashed var(--primary); background: var(--primary-light); color: var(--primary); border-radius: 8px; font-weight: 600; cursor: pointer; margin-bottom: 24px; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; }
    .btn-dashed:hover { background: rgba(200, 90, 50, 0.2); }
    
    .btn-danger-sm { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(239, 68, 68, 0.1); color: var(--danger); border: 1px solid rgba(239, 68, 68, 0.2); border-radius: 6px; cursor: pointer; transition: all 0.2s; }
    .btn-danger-sm:hover { background: var(--danger); color: white; }
    
    .total-section { text-align: right; margin-bottom: 28px; padding-top: 16px; border-top: 1px solid var(--border); }
    .total-section h2 { color: var(--text-main); margin: 0; font-size: 20px; font-weight: 700; }
    
    .footer-actions { display: flex; justify-content: flex-end; gap: 12px; border-top: 1px solid var(--border); padding-top: 24px; }
    .btn-primary { background: var(--primary); color: white; border: none; padding: 11px 20px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: background 0.2s; box-shadow: 0 2px 4px rgba(200,90,50,0.2); }
    .btn-primary:hover { background: var(--primary-dark); }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn-secondary { background: var(--bg-main); color: var(--text-main); border: 1px solid var(--border); padding: 11px 20px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: background 0.2s; display: inline-flex; align-items: center; gap: 6px; }
    .btn-secondary:hover { background: var(--border); }
    .btn-success { background: #059669; color: white; border: none; padding: 11px 20px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: background 0.2s; display: inline-flex; align-items: center; gap: 6px; box-shadow: 0 2px 4px rgba(5,150,105,0.2); }
    .btn-success:hover { background: #047857; }
    
    .action-bar { display: flex; gap: 10px; flex-wrap: wrap; }

    /* Modal Voci */
    .modal-backdrop { 
      position: fixed; 
      top: 0; left: 0; right: 0; bottom: 0;
      width: 100vw; height: 100vh; box-sizing: border-box;
      background: rgba(15, 23, 42, 0.65); 
      display: flex; justify-content: center; align-items: center; z-index: 9999; 
    }
    .modal-card { 
      background: var(--surface); padding: 32px; border-radius: 16px; width: 100%; max-width: 520px; 
      box-shadow: var(--shadow-lg); border: 1px solid var(--border); box-sizing: border-box;
    }
    .modal-card h2 { margin-top: 0; margin-bottom: 6px; color: var(--text-main); font-size: 20px; font-weight: 700; }
    .modal-actions { display: flex; justify-content: flex-end; gap: 12px; }

    .voci-preset-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-height: 300px;
      overflow-y: auto;
      margin-top: 14px;
    }
    .voce-preset-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: var(--bg-main);
      border: 1px solid var(--border);
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s;
    }
    .voce-preset-item:hover {
      background: var(--surface-hover);
      border-color: var(--primary);
    }
    .vp-info { display: flex; flex-direction: column; gap: 2px; }
    .vp-info strong { font-size: 14px; color: var(--text-main); }
    .vp-info span { font-size: 12px; color: var(--primary); font-weight: 700; }
    .voce-preset-item .material-icons { color: var(--primary); font-size: 22px; }

    @media (max-width: 768px) {
      .desktop-only { display: none !important; }
      .mobile-only { display: block !important; }
      .form-row { flex-direction: column; gap: 0; }
      input, select, textarea { font-size: 16px !important; }
      .footer-actions { flex-direction: column-reverse; gap: 10px; }
      .footer-actions button { width: 100%; }
      .action-bar { width: 100%; flex-direction: column; }
      .action-bar button { width: 100%; }
      .card { padding: 20px 16px; }
      .warning-banner { flex-direction: column; align-items: stretch; }
      .warning-banner button { width: 100%; }
      .section-voci-header { flex-direction: column; align-items: stretch; gap: 12px; }
      .btn-preset { width: 100%; justify-content: center; padding: 10px; }
      .modal-backdrop { padding: 16px; align-items: flex-end; justify-content: center; }
      .modal-card { padding: 22px 18px; border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
    }
  `]
})
export class PreventivoDetailComponent implements OnInit {
  isNew = true;
  isLocked = false;
  showMobileActions = false;
  preventivo: Preventivo = {
    data: new Date().toISOString().substring(0, 10),
    stato: 'bozza',
    note: '',
    mostra_iva: false,
    aliquota_iva: 22
  };
  clienti: Cliente[] = [];
  vociFrequenti: VoceFrequente[] = [];
  showVociModal = false;
  vociSearchTerm = '';
  righe: PreventivoRiga[] = [
    { descrizione: '', quantitativo: 1, prezzo_unitario: 0 }
  ];

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private apiService: ApiService,
    private toastService: ToastService,
    private confirmService: ConfirmService
  ) {}

  ngOnInit() {
    this.apiService.getClienti().subscribe(data => this.clienti = data);
    this.apiService.getVociFrequenti().subscribe(data => this.vociFrequenti = data);

    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'nuovo') {
      this.isNew = false;
      this.apiService.getPreventivo(parseInt(id)).subscribe(res => {
        this.preventivo = {
          ...res,
          mostra_iva: res.mostra_iva === true || (res.mostra_iva as any) === 'true',
          aliquota_iva: res.aliquota_iva != null ? Number(res.aliquota_iva) : 22
        };
        if (res.is_converted) {
          this.isLocked = true;
        }
        if (res.righe && res.righe.length > 0) {
          this.righe = res.righe;
        }
      });
    }
  }

  get filteredVociFrequenti(): VoceFrequente[] {
    if (!this.vociSearchTerm.trim()) return this.vociFrequenti;
    const term = this.vociSearchTerm.toLowerCase();
    return this.vociFrequenti.filter(v => v.descrizione.toLowerCase().includes(term));
  }

  async unlockEditing() {
    const confirmed = await this.confirmService.confirm(
      'Sblocca Modifiche Preventivo',
      `Attenzione: questo preventivo è già stato trasformato nella fattura (${this.preventivo.fattura_numero}). Modificarlo potrebbe disallineare i dati contabili. Vuoi sbloccare le modifiche?`,
      'Sblocca',
      'Annulla',
      true
    );
    if (confirmed) {
      this.isLocked = false;
      this.toastService.info('Preventivo sbloccato per le modifiche.');
    }
  }

  duplicaPreventivo() {
    this.isNew = true;
    delete this.preventivo.id;
    delete this.preventivo.numero;
    this.preventivo.stato = 'bozza';
    this.preventivo.is_converted = false;
    this.preventivo.fattura_id = undefined;
    this.preventivo.fattura_numero = undefined;
    this.isLocked = false;
    this.toastService.info('Preventivo duplicato! Seleziona un cliente (o mantieni lo stesso) e salva.');
  }

  aggiungiVocePreset(v: VoceFrequente) {
    if (this.righe.length === 1 && !this.righe[0].descrizione && this.righe[0].prezzo_unitario === 0) {
      this.righe[0] = { descrizione: v.descrizione, quantitativo: 1, prezzo_unitario: v.prezzo_unitario };
    } else {
      this.righe.push({ descrizione: v.descrizione, quantitativo: 1, prezzo_unitario: v.prezzo_unitario });
    }
    this.showVociModal = false;
    this.vociSearchTerm = '';
    this.toastService.success(`Voce "${v.descrizione}" aggiunta`);
  }

  addRiga() {
    this.righe.push({ descrizione: '', quantitativo: 1, prezzo_unitario: 0 });
  }

  removeRiga(index: number) {
    this.righe.splice(index, 1);
    if (this.righe.length === 0) {
      this.addRiga();
    }
  }

  calcTotal() {}

  calculateSubtotal(): number {
    return this.righe.reduce((sum, r) => sum + ((r.quantitativo || 0) * (r.prezzo_unitario || 0)), 0);
  }

  calculateIvaAmount(): number {
    if (!this.preventivo.mostra_iva) return 0;
    const subtotal = this.calculateSubtotal();
    const rate = this.preventivo.aliquota_iva != null ? Number(this.preventivo.aliquota_iva) : 22;
    return subtotal * (rate / 100);
  }

  calculateTotal(): number {
    const subtotal = this.calculateSubtotal();
    if (this.preventivo.mostra_iva) {
      return subtotal + this.calculateIvaAmount();
    }
    return subtotal;
  }

  save() {
    const payload = {
      ...this.preventivo,
      righe: this.righe.map((r, idx) => ({ ...r, ordine: idx + 1 }))
    };

    if (this.isNew) {
      this.apiService.createPreventivo(payload).subscribe({
        next: () => {
          this.toastService.success('Preventivo creato con successo');
          this.router.navigate(['/preventivi']);
        },
        error: (err) => this.toastService.error(err.error?.error || 'Errore durante la creazione del preventivo')
      });
    } else {
      this.apiService.updatePreventivo(this.preventivo.id!, payload).subscribe({
        next: () => {
          this.toastService.success('Preventivo aggiornato con successo');
          this.router.navigate(['/preventivi']);
        },
        error: (err) => this.toastService.error(err.error?.error || 'Errore durante l\'aggiornamento del preventivo')
      });
    }
  }

  async trasformaInFattura() {
    const confirmed = await this.confirmService.confirm(
      'Trasforma in Fattura',
      'Vuoi trasformare questo preventivo in una fattura? Verranno copiati i dati e le voci.',
      'Trasforma',
      'Annulla'
    );
    if (confirmed) {
      const aliquotaToUse = this.preventivo.mostra_iva && this.preventivo.aliquota_iva != null ? Number(this.preventivo.aliquota_iva) : 22.00;
      const nuovaFattura = {
        cliente_id: this.preventivo.cliente_id,
        preventivo_id: this.preventivo.id,
        tipo: 'fattura',
        numero: 'FAT-' + new Date().getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000),
        data_emissione: new Date().toISOString().substring(0, 10),
        stato: 'bozza',
        nota: `Generato da Preventivo #${this.preventivo.id}`,
        righe: this.righe.map(r => ({
          descrizione: r.descrizione,
          quantitativo: r.quantitativo,
          prezzo_unitario: r.prezzo_unitario,
          aliquota_iva: aliquotaToUse
        }))
      };

      this.apiService.createFattura(nuovaFattura as any).subscribe({
        next: (res: any) => {
          this.toastService.success('Preventivo trasformato in fattura con successo!');
          this.router.navigate(['/fatture', res.id]);
        },
        error: (err) => this.toastService.error(err.error?.error || 'Errore durante la conversione in fattura')
      });
    }
  }

  downloadPdf() {
    if (!this.preventivo.id) return;
    this.apiService.downloadPreventivoPdf(this.preventivo.id).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preventivo_${this.preventivo.id}.pdf`;
      a.click();
    });
  }

  sendEmail() {
    if (!this.preventivo.id! || !this.preventivo.id) return;
    this.apiService.sendPreventivoEmail(this.preventivo.id).subscribe({
      next: () => this.toastService.success('Email inviata con successo!'),
      error: (err) => this.toastService.error(err.error?.error || 'Errore invio email')
    });
  }
}
